import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import type { OutlineTemplate } from '@/lib/api'

interface TemplatePreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: OutlineTemplate | null
  currentUserId: string
}

export function TemplatePreviewDialog({
  open,
  onOpenChange,
  template,
  currentUserId,
}: TemplatePreviewDialogProps) {
  if (!template) return null

  const isCreator = template.createdBy === currentUserId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-2">
              <span>{isCreator ? 'You' : template.creatorName}</span>
              <span>·</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                {template.visibility === 'shared' ? 'Shared' : 'Private'}
              </Badge>
              <span>·</span>
              <span>{template.sections.length} {template.sections.length === 1 ? 'section' : 'sections'}</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 px-1 -mx-1">
          {/* Description */}
          {template.description && (
            <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
          )}

          {/* Sections */}
          <div className="space-y-1">
            {template.sections.map((section, index) => (
              <div key={index} className="flex items-start gap-3 py-2 border-b last:border-b-0">
                <span className="text-meta mt-0.5 w-5 flex-shrink-0 text-right">{index + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{section.title}</p>
                  {section.description && (
                    <p className="text-meta mt-0.5">{section.description}</p>
                  )}
                </div>
                {section.targetWordCount && (
                  <span className="text-meta flex-shrink-0">
                    {section.targetWordCount.toLocaleString()} words
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
